import * as utils from '@/common/utils'

/**
 * Returns all properties and their default state.
 *
 * @param type String Can be either 'unit' or 'template'
 *
 * @return Object
 */
export const properties = (type = 'unit') => {
  const standardProps = {
    id: null,

    // Basic Options
    ign_connected: type === 'template' ? null : true,
    ignition: type === 'template' ? null : 'input_01',
    fixed_latitude: null,
    fixed_longitude: null,
    allow_geo_lookups: type === 'template' ? null : true,
    perform_post_processing: type === 'template' ? null : true,
    post_process_trip_stops: type === 'template' ? null : true,
    auto_correct_direction: type === 'template' ? null : true,
    unit_type: type === 'template' ? null : 'acmtech',
    unit_model: type === 'template' ? null : 'acmtrack_1',
    unit_status: type === 'template' ? null : 'factory',
    timezone: type === 'template' ? null : 'UTC',
    op_timezone: type === 'template' ? null : 'auto',
    // null means "Raw only", '-1' means "Do not change" (templates only)
    canbus_profile: type === 'template' ? '-1' : null,

    // Trip Starts
    trip_start_enabled: type === 'template' ? null : true,
    trip_start_distance_value: 50,
    trip_start_distance_unit: 'meters',
    trip_start_speed_value: 10,
    trip_start_speed_unit: 'kmh',
    trip_start_event: 'TRIP_START',

    // Trip Stops
    trip_stop_enabled: type === 'template' ? null : true,
    trip_stop_time_value: 5,
    trip_stop_time_unit: 'minutes',
    trip_stop_event: 'TRIP_STOP',

    // Comm Failures
    comms_failure_enabled: type === 'template' ? null : true,
    comms_failure_after_value: 60,
    comms_failure_after_unit: 'seconds',
    comms_failure_report_value: 24,
    comms_failure_report_unit: 'hours',
    comms_failure_event: 'NO_COMMS',
    comms_restore_event: 'COMMS_OK',

    // GPS Drifting
    gps_drifting_enabled: type === 'template' ? null : true,
    gps_drifting_radius_value: 50,
    gps_drifting_radius_unit: 'meters',
    gps_drifting_speed_value: 10,
    gps_drifting_speed_unit: 'kmh',
    gps_drifting_event: 'GPS_DRIFTING',

    // GPS Spiking
    gps_spiking_enabled: type === 'template' ? null : true,
    gps_spiking_speed_value: 250,
    gps_spiking_speed_unit: 'kmh',
    gps_spiking_event: 'GPS_SPIKE',

    // Idling
    idling_enabled: type === 'template' ? null : true,
    idling_speed_value: 1,
    idling_speed_unit: 'kmh',
    idling_start_event: 'ENG_IDLE:START',
    idling_stop_event: 'ENG_IDLE:STOP',

    // Ignition
    ignition_enabled: type === 'template' ? null : true,
    ignition_event: 'IGN_FAULTY',

    // Standard SIM card
    sim_card: {
      id: null,
      cell_no: null,
      iccid: null,
      gdsp_id: null,
      provider_id: null,
      provider_code: null,
      provider_name: null,
      note: null
    },

    // Sensors
    sensors: [],

    // Digital Inputs
    standard_inputs: [],

    // Auxiliary Inputs
    auxiliary_inputs: [],

    // Analog Inputs
    analog_inputs: [],

    // Event Code Conversions
    event_conversions: [],
    photos: [],

    // Timestamps
    created_at: null,
    updated_at: null,

    // Other properties used for store module/state
    selectedSensorIndex: '-1',
    removingSensorIndex: null
  }

  if (type === 'template') {
    return {
      // General Details
      name: '',
      description: '',
      user_id: null,
      user_full_name: null,
      ...standardProps
    }
  } else {
    return {
      // General Details
      imei: null,
      serial_no: null,
      stock_no: null,
      name: null,

      // Assignments
      partner: { id: null, name: null },
      agency: { id: null, name: null },
      client: { id: null, name: null },
      unit_group: { id: null, name: '' },

      template_id: null,
      vehicle: null,
      vehicle_id: null,
      assigned_as_id: 'primary',
      monitored_event_profile_id: null,
      monitored_event_profile_name: null,
      client_event_profile_id: null,
      client_event_profile_name: null,
      gateway_route: null,
      gateway_route_id: null,
      note: '',

      photos: [],

      vehicleAssignments: [],
      gatewayRouteAssignments: [],
      vehicleIsAssigned: false,
      isVehiclesLoading: false,
      isGatewayRoutesLoading: false,
      gatewayRouteIsAssigned: false,
      savedClientId: null,
      orgAssignMode: null, // can be null, 'client', 'agency', 'partner'

      ...standardProps
    }
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param unit Object
 * @param type String Can be either 'unit' or 'template'
 * @param notes Array|null
 * @param attachments Array|null
 * @param photos Array|null
 *
 * @return Object
 */
export const transformCreate = (unit, type = 'unit', notes = null, attachments = null, photos = null) => {
  const standardProps = {
    unit_type_code: unit.unit_type,
    unit_model_code: unit.unit_model,
    unit_status_code: unit.unit_status,
    timezone: unit.timezone,
    op_timezone: unit.op_timezone,
    ignition: unit.ignition,
    ...getCanbusProfile(unit, type),
    flags: {
      ign_connected: unit.ign_connected,
      allow_geo_lookups: unit.allow_geo_lookups,
      perform_post_processing: unit.perform_post_processing,
      post_process_trip_stops: unit.post_process_trip_stops,
      auto_correct_direction: unit.auto_correct_direction
    },
    system: {
      trip_start: getTripStartOptions(unit),
      trip_stop: getTripStopOptions(unit),
      comms_failure: getCommsFailureOptions(unit),
      gps_drifting: getGPSDriftingOptions(unit),
      gps_spiking: getGPSSpikingOptions(unit),
      faulty_ignition: getFaultyIgnitionOptions(unit),
      idling: getEngineIdleOptions(unit)
    },
    sensors: unit.sensors.map(sensor => transformSensor(sensor)),
    standard_inputs: unit.standard_inputs.map((input) => {
      return {
        input_no: input.input_no,
        description: utils.asStringOrNull(input.description.trim()),
        low_event: utils.asStringOrNull(input.low_event.trim()),
        high_event: utils.asStringOrNull(input.high_event.trim()),
        submit_when: input.submit_when
      }
    }),
    auxiliary_inputs: unit.auxiliary_inputs.map((input) => {
      return {
        input_no: input.input_no,
        description: utils.asStringOrNull(input.description.trim()),
        low_event: utils.asStringOrNull(input.low_event.trim()),
        high_event: utils.asStringOrNull(input.high_event.trim()),
        submit_when: input.submit_when
      }
    }),
    analog_inputs: unit.analog_inputs.map((input) => {
      return {
        input_no: input.input_no,
        label: utils.asStringOrNull(input.label.trim()),
        serial_no: utils.asStringOrNull(input.serial_no.trim()),
        input_type: utils.asStringOrNull(input.input_type.trim()),
        low_raw_limit: utils.asNumberOrNull(input.low_raw_limit),
        high_raw_limit: utils.asNumberOrNull(input.high_raw_limit),
        factor: utils.asNumberOrNull(input.factor),
        uom: utils.asStringOrNull(input.uom.trim()),
        low_value: utils.asNumberOrNull(input.low_value),
        low_event_code: utils.asStringOrNull(input.low_event_code.trim()),
        high_value: utils.asNumberOrNull(input.high_value),
        high_event_code: utils.asStringOrNull(input.high_event_code.trim())
      }
    }),
    event_conversions: unit.event_conversions.map((eventConversion) => {
      return {
        event_from: eventConversion.event_from.trim(),
        event_to: eventConversion.event_to.trim(),
        description: utils.asStringOrNull(eventConversion.description.trim())
      }
    })
  }

  let vehicle_id = unit.vehicle_id === null ? unit.vehicle_id : parseInt(unit.vehicle_id)
  let assigned_as = unit.vehicle_id === null ? null : unit.assigned_as_id
  let gateway_route_id = unit.gateway_route_id === null ? unit.gateway_route_id : parseInt(unit.gateway_route_id)

  if (type === 'template') {
    return {
      name: unit.name,
      description: unit.description,
      ...standardProps
    }
  } else {
    let unit_group_id = unit.unit_group.id === null ? null : parseInt(unit.unit_group.id)
    let monitored_event_profile_id = unit.monitored_event_profile_id === null ? null : parseInt(unit.monitored_event_profile_id)
    let client_event_profile_id = unit.client_event_profile_id === null ? null : parseInt(unit.client_event_profile_id)

    return {
      imei: unit.imei,
      serial_no: unit.serial_no,
      stock_no: unit.stock_no,
      name: unit.name,
      partner_id: unit.partner.id,
      agency_id: unit.agency.id,
      client_id: unit.client.id,
      unit_group_id: unit_group_id,
      vehicle_id: vehicle_id,
      gateway_route_id: gateway_route_id,
      monitored_event_profile_id: monitored_event_profile_id,
      client_event_profile_id: client_event_profile_id,
      assigned_as: assigned_as,
      fixed_latitude: unit.fixed_latitude,
      fixed_longitude: unit.fixed_longitude,
      sim_card_id: unit.sim_card.id,
      notes,
      attachments,
      photos,
      ...standardProps
    }
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param unit Object
 *
 * @return Object
 */
export const transformBasicUpdate = (unit) => {
  return {
    id: unit.id,
    name: unit.name,
    unit_group_id: unit.unit_group.id === null ? null : parseInt(unit.unit_group.id)
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param unit Object
 * @param type String Can be either 'unit' or 'template'
 *
 * @return Object
 */
export const transformUpdate = (unit, type = 'unit') => {
  const standardProps = {
    id: unit.id,
    unit_type_code: unit.unit_type,
    unit_model_code: unit.unit_model,
    unit_status_code: unit.unit_status,
    timezone: unit.timezone,
    op_timezone: unit.op_timezone,
    ignition: unit.ignition,
    ...getCanbusProfile(unit, type),
    flags: {
      ign_connected: unit.ign_connected,
      allow_geo_lookups: unit.allow_geo_lookups,
      perform_post_processing: unit.perform_post_processing,
      post_process_trip_stops: unit.post_process_trip_stops,
      auto_correct_direction: unit.auto_correct_direction
    },
    system: {
      trip_start: getTripStartOptions(unit),
      trip_stop: getTripStopOptions(unit),
      comms_failure: getCommsFailureOptions(unit),
      gps_drifting: getGPSDriftingOptions(unit),
      gps_spiking: getGPSSpikingOptions(unit),
      faulty_ignition: getFaultyIgnitionOptions(unit),
      idling: getEngineIdleOptions(unit)
    },
    sensors: unit.sensors.map(sensor => transformSensor(sensor)),
    standard_inputs: unit.standard_inputs.map((input) => {
      return {
        input_no: parseInt(input.input_no, 10),
        description: utils.asStringOrNull(input.description ? input.description.trim() : null),
        low_event: utils.asStringOrNull(input.low_event ? input.low_event.trim() : null),
        high_event: utils.asStringOrNull(input.high_event ? input.high_event.trim() : null),
        submit_when: input.submit_when
      }
    }),
    auxiliary_inputs: unit.auxiliary_inputs.map((input) => {
      return {
        input_no: parseInt(input.input_no, 10),
        description: utils.asStringOrNull(input.description ? input.description.trim() : null),
        low_event: utils.asStringOrNull(input.low_event ? input.low_event.trim() : null),
        high_event: utils.asStringOrNull(input.high_event ? input.high_event.trim() : null),
        submit_when: input.submit_when
      }
    }),
    analog_inputs: unit.analog_inputs.map((input) => {
      return {
        input_no: parseInt(input.input_no, 10),
        label: utils.asStringOrNull(input.label ? input.label.trim() : null),
        serial_no: utils.asStringOrNull(input.serial_no ? input.serial_no.trim() : null),
        input_type: utils.asStringOrNull(input.input_type ? input.input_type.trim() : null),
        low_raw_limit: utils.asNumberOrNull(input.low_raw_limit),
        high_raw_limit: utils.asNumberOrNull(input.high_raw_limit),
        factor: utils.asNumberOrNull(input.factor),
        uom: utils.asStringOrNull(input.uom ? input.uom.trim() : null),
        low_value: utils.asNumberOrNull(input.low_value),
        low_event_code: utils.asStringOrNull(input.low_event_code ? input.low_event_code.trim() : null),
        high_value: utils.asNumberOrNull(input.high_value),
        high_event_code: utils.asStringOrNull(input.high_event_code ? input.high_event_code.trim() : null)
      }
    }),
    event_conversions: unit.event_conversions.map((eventConversion) => {
      return {
        event_from: eventConversion.event_from ? eventConversion.event_from.trim() : null,
        event_to: eventConversion.event_to ? eventConversion.event_to.trim() : null,
        description: utils.asStringOrNull(
          eventConversion.description ? eventConversion.description.trim() : null)
      }
    })
  }

  if (type === 'template') {
    return {
      name: unit.name,
      description: unit.description,
      ...standardProps
    }
  } else {
    let monitored_event_profile_id = unit.monitored_event_profile_id === null ? null : parseInt(unit.monitored_event_profile_id)
    let client_event_profile_id = unit.client_event_profile_id === null ? null : parseInt(unit.client_event_profile_id)

    return {
      imei: unit.imei,
      serial_no: unit.serial_no,
      stock_no: unit.stock_no,
      name: unit.name,
      partner_id: unit.partner.id,
      agency_id: unit.agency.id,
      client_id: unit.client.id,
      unit_group_id: unit.unit_group.id === null ? null : parseInt(unit.unit_group.id),
      vehicle_id: parseInt(unit.vehicle_id),
      assigned_as: unit.vehicle_id === null ? null : unit.assigned_as_id,
      gateway_route_id: parseInt(unit.gateway_route_id),
      monitored_event_profile_id: monitored_event_profile_id,
      client_event_profile_id: client_event_profile_id,
      fixed_latitude: unit.fixed_latitude,
      fixed_longitude: unit.fixed_longitude,
      sim_card_id: unit.sim_card.id,
      ...standardProps
    }
  }
}

export const transformBasicLoad = (unit) => {
  let unit_group = { id: null, name: null }
  if (unit.hasOwnProperty('unit_group') && unit.unit_group !== null) {
    unit_group = Object.assign({}, unit.unit_group)
  }

  return {
    id: unit.id,
    imei: unit.imei,
    serial_no: unit.serial_no,
    name: unit.name,

    unit_type_code: unit.unit_type_code,
    unit_type_name: unit.unit_type_name,
    unit_model_code: unit.unit_model_code,
    unit_model_name: unit.unit_model_name,

    // Assignments
    partner: unit.partner,
    agency: unit.agency,
    client: unit.client,
    unit_group: unit_group
  }
}

/**
 * Transforms a previously loaded unit from the API to the
 * state object that we'll be working with. This is effectively
 * the reverse of transformCreate and transformUpdate.
 *
 * @param unit Object
 * @param type String Can be either 'unit' or 'template'
 *
 * @return Object
 */
export const transformLoad = (unit, type = 'unit') => {
  const standardProps = {
    id: unit.id,

    // Basic Options
    ign_connected: unit.flags.ign_connected,
    ignition: unit.ignition,
    fixed_latitude: unit.fixed_latitude,
    fixed_longitude: unit.fixed_longitude,
    allow_geo_lookups: unit.flags.allow_geo_lookups,
    perform_post_processing: unit.flags.perform_post_processing,
    post_process_trip_stops: unit.flags.post_process_trip_stops,
    auto_correct_direction: unit.flags.auto_correct_direction,
    unit_type: unit.unit_type_code,
    unit_model: unit.unit_model_code,
    unit_status: unit.unit_status_code,
    timezone: unit.timezone,
    op_timezone: unit.op_timezone,
    canbus_profile: getLoadedCanbusProfile(unit, type),
    telemetry: typeof unit.telemetry === 'undefined' ? null : unit.telemetry,

    // Trip Start
    trip_start_enabled: unit.system.trip_start.enabled,
    trip_start_distance_value: utils.extractUoMValue(unit.system.trip_start.distance),
    trip_start_distance_unit: utils.extractUoMUnit(unit.system.trip_start.distance),
    trip_start_speed_value: utils.extractUoMValue(unit.system.trip_start.speed),
    trip_start_speed_unit: utils.extractUoMUnit(unit.system.trip_start.speed),
    trip_start_event: unit.system.trip_start.event,

    // Trip Stop
    trip_stop_enabled: unit.system.trip_stop.enabled,
    trip_stop_time_value: utils.extractUoMValue(unit.system.trip_stop.time),
    trip_stop_time_unit: utils.extractUoMUnit(unit.system.trip_stop.time),
    trip_stop_event: unit.system.trip_stop.event,

    // Comm Failures
    comms_failure_enabled: unit.system.comms_failure.enabled,
    comms_failure_after_value: utils.extractUoMValue(unit.system.comms_failure.after),
    comms_failure_after_unit: utils.extractUoMUnit(unit.system.comms_failure.after),
    comms_failure_report_value: utils.extractUoMValue(unit.system.comms_failure.report_every),
    comms_failure_report_unit: utils.extractUoMUnit(unit.system.comms_failure.report_every),
    comms_failure_event: unit.system.comms_failure.event,
    comms_restore_event: unit.system.comms_failure.restored_event,

    // GPS Drifting
    gps_drifting_enabled: unit.system.gps_drifting.enabled,
    gps_drifting_radius_value: utils.extractUoMValue(unit.system.gps_drifting.radius),
    gps_drifting_radius_unit: utils.extractUoMUnit(unit.system.gps_drifting.radius),
    gps_drifting_speed_value: utils.extractUoMValue(unit.system.gps_drifting.speed),
    gps_drifting_speed_unit: utils.extractUoMUnit(unit.system.gps_drifting.speed),
    gps_drifting_event: unit.system.gps_drifting.event,

    // GPS Spiking
    gps_spiking_enabled: unit.system.gps_spiking.enabled,
    gps_spiking_speed_value: utils.extractUoMValue(unit.system.gps_spiking.speed),
    gps_spiking_speed_unit: utils.extractUoMUnit(unit.system.gps_spiking.speed),
    gps_spiking_event: unit.system.gps_spiking.event,

    // Ignition
    ignition_enabled: unit.system.faulty_ignition.enabled,
    ignition_event: unit.system.faulty_ignition.event,

    // Idling
    idling_enabled: unit.system.idling.enabled,
    idling_speed_value: utils.extractUoMValue(unit.system.idling.speed_less_than),
    idling_speed_unit: utils.extractUoMUnit(unit.system.idling.speed_less_than),
    idling_start_event: unit.system.idling.event_idle_start,
    idling_stop_event: unit.system.idling.event_idle_stop,

    // Sensors
    sensors: unit.sensors,

    // Standard Inputs
    standard_inputs: unit.standard_inputs.map((input) => {
      return {
        input_no: input.input_no,
        description: input.description,
        low_event: input.low_event,
        high_event: input.high_event,
        submit_when: input.submit_when
      }
    }),

    // Auxiliary Inputs
    auxiliary_inputs: unit.auxiliary_inputs.map((input) => {
      return {
        input_no: input.input_no,
        description: input.description,
        low_event: input.low_event,
        high_event: input.high_event,
        submit_when: input.submit_when
      }
    }),

    // Analog Inputs
    analog_inputs: unit.analog_inputs.map((input) => {
      return {
        input_no: input.input_no,
        label: input.label,
        serial_no: input.serial_no,
        input_type: input.input_type,
        low_raw_limit: input.low_raw_limit,
        high_raw_limit: input.high_raw_limit,
        factor: input.factor,
        uom: input.uom,
        low_value: input.low_value,
        low_event_code: input.low_event_code,
        high_value: input.high_value,
        high_event_code: input.high_event_code
      }
    }),

    // Event Conversions
    event_conversions: unit.event_conversions.map((eventConversion) => {
      return {
        event_from: eventConversion.event_from,
        event_to: eventConversion.event_to,
        description: eventConversion.description
      }
    }),

    // Timestamps
    created_at: unit.created_at,
    updated_at: unit.updated_at,

    // Other properties used for store module/state
    removingSensorIndex: null,
    selectedSensorIndex: '-1'
  }

  if (type === 'template') {
    return {
      name: unit.name,
      description: unit.description,
      user_id: unit.user_id,
      user_full_name: unit.user_full_name,
      ...standardProps
    }
  } else {
    let unit_group = { id: null, name: null }
    if (unit.hasOwnProperty('unit_group') && unit.unit_group !== null) {
      unit_group = Object.assign({}, unit.unit_group)
    }

    let gateway_route = { id: null }
    if (unit.hasOwnProperty('gateway_route') && unit.gateway_route !== null) {
      gateway_route = Object.assign({}, unit.gateway_route)
    }

    return {
      imei: unit.imei,
      serial_no: unit.serial_no,
      stock_no: unit.stock_no,
      name: unit.name,

      // Assignments
      partner: unit.partner,
      agency: unit.agency,
      client: unit.client,
      unit_group: unit_group,

      // Assignments
      vehicle: unit.vehicle,
      vehicle_id: unit.vehicle !== null ? unit.vehicle.id : null,
      assigned_as_id: unit.vehicle !== null ? unit.vehicle.assigned_as : 'primary',
      monitored_event_profile_id: unit.monitored_event_profile_id,
      monitored_event_profile_name: unit.monitored_event_profile_name,
      client_event_profile_id: unit.client_event_profile_id,
      client_event_profile_name: unit.client_event_profile_name,
      template_id: unit.template_id,
      gateway_route: gateway_route,
      gateway_route_id: gateway_route.id,

      sim_card: {
        id: unit.sim_card.id,
        cell_no: unit.sim_card.cell_no,
        iccid: unit.sim_card.iccid,
        gdsp_id: unit.sim_card.gdsp_id,
        provider_id: unit.sim_card.provider_id,
        provider_code: unit.sim_card.provider_code,
        provider_name: unit.sim_card.provider_name,
        note: unit.sim_card.note
      },

      vehicleAssignments: [],
      gatewayRouteAssignments: [],
      vehicleIsAssigned: false,
      isVehiclesLoading: false,
      gatewayRouteIsAssigned: false,
      isGatewayRoutesLoading: false,
      savedClientId: null,
      orgAssignMode: null, // can be null, 'client', 'agency', 'partner'

      ...standardProps
    }
  }
}

function transformSensor (sensor) {
  return {
    id: sensor.id,
    enabled: sensor.enabled,
    fields: sensor.fields.map(field => {
      return {
        name: field.name,
        value: convertSensorValue(field.value, field.type),
        symbol: field.symbol
      }
    })
  }
}

function convertSensorValue (value, type) {
  if (value === null) {
    return null
  }

  switch (type) {
    case 'integer':
      return parseInt(value, 10)

    case 'float':
      return parseFloat(value)

    case 'boolean':
      return value === true || value !== 0 || value === 'yes' || value === '1' || value === 'true'

    default:
      return String(value)
  }
}

/**
 * Returns the CAN profile property to send to the API. A template's
 * "Do not change" ('-1') leaves the property out.
 *
 * @param unit Object
 * @param type String Can be either 'unit' or 'template'
 *
 * @return Object
 */
function getCanbusProfile (unit, type) {
  if (type === 'template' && unit.canbus_profile === '-1') {
    return {}
  }

  return { canbus_profile: unit.canbus_profile }
}

/**
 * Returns the CAN profile of a unit or template loaded from the API.
 * A template without the property means "Do not change" ('-1').
 *
 * @param unit Object
 * @param type String Can be either 'unit' or 'template'
 *
 * @return String|null
 */
function getLoadedCanbusProfile (unit, type) {
  if (unit.hasOwnProperty('canbus_profile')) {
    return unit.canbus_profile
  }

  return type === 'template' ? '-1' : null
}

function getTripStartOptions (unit) {
  if (unit.trip_start_enabled !== '') {
    return {
      enabled: unit.trip_start_enabled,
      distance: getCombinedValue(unit, unit.trip_start_distance_value, unit.trip_start_distance_unit),
      speed: getCombinedValue(unit, unit.trip_start_speed_value, unit.trip_start_speed_unit),
      event: getValue(unit.trip_start_event)
    }
  } else {
    return null
  }
}

function getTripStopOptions (unit) {
  if (unit.trip_stop_enabled !== '') {
    return {
      enabled: unit.trip_stop_enabled,
      time: getCombinedValue(unit, unit.trip_stop_time_value, unit.trip_stop_time_unit),
      event: getValue(unit.trip_stop_event)
    }
  } else {
    return null
  }
}

function getCommsFailureOptions (unit) {
  if (unit.comms_failure_enabled !== '') {
    return {
      enabled: unit.comms_failure_enabled,
      after: getCombinedValue(unit, unit.comms_failure_after_value, unit.comms_failure_after_unit),
      report_every: getCombinedValue(unit, unit.comms_failure_report_value, unit.comms_failure_report_unit),
      event: getValue(unit.comms_failure_event),
      restored_event: getValue(unit.comms_restore_event)
    }
  } else {
    return null
  }
}

function getGPSDriftingOptions (unit) {
  if (unit.gps_drifting_enabled !== '') {
    return {
      enabled: unit.gps_drifting_enabled,
      radius: getCombinedValue(unit, unit.gps_drifting_radius_value, unit.gps_drifting_radius_unit),
      speed: getCombinedValue(unit, unit.gps_drifting_speed_value, unit.gps_drifting_speed_unit),
      event: getValue(unit.gps_drifting_event)
    }
  } else {
    return null
  }
}

function getGPSSpikingOptions (unit) {
  if (unit.gps_spiking_enabled !== '') {
    return {
      enabled: unit.gps_spiking_enabled,
      speed: getCombinedValue(unit, unit.gps_spiking_speed_value, unit.gps_spiking_speed_unit),
      event: getValue(unit.gps_spiking_event)
    }
  } else {
    return null
  }
}

function getFaultyIgnitionOptions (unit) {
  if (unit.ignition_enabled !== '') {
    return {
      enabled: unit.ignition_enabled,
      event: getValue(unit.ignition_event)
    }
  } else {
    return null
  }
}

function getEngineIdleOptions (unit) {
  if (unit.idling_enabled !== '') {
    return {
      enabled: unit.idling_enabled,
      speed_less_than: getCombinedValue(unit, unit.idling_speed_value, unit.idling_speed_unit),
      event_idle_start: getValue(unit.idling_start_event),
      event_idle_stop: getValue(unit.idling_stop_event)
    }
  } else {
    return null
  }
}

function getCombinedValue (unit, value, units) {
  if (!value) {
    return null
  } else {
    return `${value} ${units}`
  }
}

function getValue (value) {
  if (!value) {
    return null
  } else {
    return value
  }
}



// WEBPACK FOOTER //
// ./src/store/units/unitsHelper.js